import React, { useState } from "react";

import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, CircularProgress, IconButton, Menu, MenuItem, Stack, Typography } from "@mui/material";

const ZOOM_PRESETS = [25, 50, 75, 100, 125, 150, 200];

interface Props {
  zoomPercent: number;
  pageNumber: number;
  numPages: number;
  loading?: boolean;
  onZoomChange: (scale: number) => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onPageChange: (page: number) => void;
}

export const PDFToolbar: React.FC<Props> = React.memo(
  ({ zoomPercent, pageNumber, numPages, loading, onZoomChange, onZoomIn, onZoomOut, onPageChange }) => {
    const [zoomMenuAnchor, setZoomMenuAnchor] = useState<HTMLElement | null>(null);
    const [pageMenuAnchor, setPageMenuAnchor] = useState<HTMLElement | null>(null);

    return (
      <Stack
        direction="row"
        alignItems="center"
        gap="10px"
        sx={{
          position: "absolute",
          top: "12px",
          right: "12px",
          zIndex: 1500,
          bgcolor: "white",
          borderRadius: "4px",
          boxShadow: "0px 1px 4px 0px rgba(18,18,23,0.12), 0px 1px 2px 0px rgba(18,18,23,0.08)",
          p: "5px"
        }}
      >
        {loading && <CircularProgress size={16} sx={{ color: "#071f35" }} />}
        <Stack direction="row" alignItems="center" gap="5px">
          <Box
            onClick={(e) => setZoomMenuAnchor(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "80px",
              border: "1px solid #7b92a9",
              borderRadius: "8px",
              px: "8px",
              py: "8px",
              cursor: "pointer",
              "&:hover": { borderColor: "#071f35" }
            }}
          >
            <Typography sx={{ flex: 1, fontSize: "14px", lineHeight: "16px", color: "#071f35", whiteSpace: "nowrap" }}>
              {zoomPercent}%
            </Typography>
            <ArrowDropDownIcon sx={{ fontSize: 16, color: "#071f35" }} />
          </Box>
          <Menu
            anchorEl={zoomMenuAnchor}
            open={Boolean(zoomMenuAnchor)}
            onClose={() => setZoomMenuAnchor(null)}
            slotProps={{ paper: { sx: { borderRadius: "8px", minWidth: "80px" } } }}
          >
            {ZOOM_PRESETS.map((val) => (
              <MenuItem
                key={val}
                selected={zoomPercent === val}
                onClick={() => {
                  onZoomChange(val / 100);
                  setZoomMenuAnchor(null);
                }}
                sx={{ fontSize: "14px", px: "8px", py: "4px" }}
              >
                {val}%
              </MenuItem>
            ))}
          </Menu>
          <IconButton onClick={onZoomIn} sx={{ padding: "6px", borderRadius: "32px" }}>
            <AddIcon sx={{ fontSize: 20, color: "#071f35" }} />
          </IconButton>
          <IconButton onClick={onZoomOut} sx={{ padding: "6px", borderRadius: "32px" }}>
            <RemoveIcon sx={{ fontSize: 20, color: "#071f35" }} />
          </IconButton>
        </Stack>

        <Box sx={{ width: "1px", alignSelf: "stretch", bgcolor: "#dbe8f5" }} />

        <Stack direction="row" alignItems="center" gap="5px">
          <Typography sx={{ fontSize: "16px", lineHeight: "24px", color: "#071f35", whiteSpace: "nowrap" }}>
            Page:
          </Typography>
          <Box
            onClick={numPages > 1 ? (e) => setPageMenuAnchor(e.currentTarget) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              width: "60px",
              border: "1px solid #7b92a9",
              borderRadius: "8px",
              px: "8px",
              py: "8px",
              cursor: numPages > 1 ? "pointer" : "default",
              opacity: numPages > 1 ? 1 : 0.5,
              "&:hover": numPages > 1 ? { borderColor: "#071f35" } : {}
            }}
          >
            <Typography sx={{ flex: 1, fontSize: "14px", lineHeight: "16px", color: "#071f35", whiteSpace: "nowrap" }}>
              {pageNumber}
            </Typography>
            <ArrowDropDownIcon sx={{ fontSize: 16, color: "#071f35" }} />
          </Box>
          <Menu
            anchorEl={pageMenuAnchor}
            open={Boolean(pageMenuAnchor)}
            onClose={() => setPageMenuAnchor(null)}
            slotProps={{ paper: { sx: { borderRadius: "8px", minWidth: "60px", maxHeight: 300 } } }}
          >
            {Array.from({ length: numPages || 0 }, (_, i) => i + 1).map((page) => (
              <MenuItem
                key={page}
                selected={pageNumber === page}
                onClick={() => {
                  onPageChange(page);
                  setPageMenuAnchor(null);
                }}
                sx={{ fontSize: "14px", px: "8px", py: "4px" }}
              >
                {page}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </Stack>
    );
  }
);
